import { useState, useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import type { AttributeField } from "@/features/properties/types/propertyAttributes";
import type { CatalogCrosswalk } from "./useSurveyCatalogCrosswalk";
import { toast } from "sonner";

const db = supabase as unknown as any;

/**
 * Editing state + persistence for a site survey. `updateField*` patch the in-memory
 * tree and debounce-save. On save we DUAL-WRITE: the legacy `data` column (kept as
 * the structural shell / fallback) AND `prod_data` (the new canonical answer store)
 * for the questions that changed — merged so QA-reviewed / other entries are preserved.
 */
export function useSiteSurveyEditor(attributeId: string | undefined, catalog?: CatalogCrosswalk) {
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<AttributeField[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Leaf ids changed since the last successful save → which prod_data entries to write.
  const changedIdsRef = useRef<Set<string>>(new Set());

  const catalogRef = useRef<CatalogCrosswalk | undefined>(catalog);
  catalogRef.current = catalog;

  /** Initialize the state from a parsed data blob (or resolved tree) */
  const initializeFields = useCallback((data: unknown) => {
    if (!data) {
      setFields([]);
      changedIdsRef.current.clear();
      return;
    }
    const parsed: AttributeField[] =
      typeof data === "string" ? JSON.parse(data) : (data as AttributeField[]);
    setFields(parsed);
    setIsDirty(false);
    changedIdsRef.current.clear();
  }, []);

  const scheduleSave = useCallback(() => {
    setIsDirty(true);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveMutation.mutate(), 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Deep-update a single leaf field's value by id */
  const updateFieldValue = useCallback(
    (fieldId: string, newValue: string | number | boolean | string[] | null) => {
      setFields((prev) => {
        const cloned = structuredClone(prev);
        updateValueInTree(cloned, fieldId, newValue);
        return cloned;
      });
      changedIdsRef.current.add(fieldId);
      scheduleSave();
    },
    [scheduleSave]
  );

  /** Deep-update a single leaf field's metadata by id */
  const updateFieldMetadata = useCallback(
    (
      fieldId: string,
      patch: { confidence?: number | null; evidence?: string[]; notes?: string | null }
    ) => {
      setFields((prev) => {
        const cloned = structuredClone(prev);
        mergeMetadataInTree(cloned, fieldId, patch);
        return cloned;
      });
      changedIdsRef.current.add(fieldId);
      scheduleSave();
    },
    [scheduleSave]
  );

  /** Persist mutation — dual-write data + prod_data */
  const saveMutation = useMutation({
    mutationFn: async (): Promise<{ deferred: boolean }> => {
      if (!attributeId) throw new Error("No attribute ID");

      const cat = catalogRef.current;
      const changed = changedIdsRef.current;

      // Don't do a partial write: if there are answer changes to sync but the
      // catalog crosswalk hasn't loaded yet, DEFER. Writing now would update
      // `data` but skip `prod_data`, leaving the canonical store (what
      // resolveSurveyAttributes reads) stale — and onSuccess would clear the
      // changed ids, so it'd never re-sync. Retry once the catalog is ready.
      if (changed.size > 0 && !cat) return { deferred: true };

      const update: Record<string, unknown> = { data: fieldsRef.current };

      // Sync changed answers into prod_data (canonical). Merge into the row's
      // existing prod_data so QA-reviewed and untouched entries are preserved.
      if (cat && changed.size > 0) {
        const entries = collectChangedEntries(fieldsRef.current, changed, cat, new Date().toISOString());
        if (Object.keys(entries).length > 0) {
          const { data: row, error: readError } = await db
            .from("property_attributes")
            .select("prod_data")
            .eq("id", attributeId)
            .maybeSingle();
          // Abort on a failed read — merging onto an empty {} would overwrite
          // the existing prod_data (QA decisions + every other answer).
          if (readError) throw readError;
          const current = (row?.prod_data as Record<string, unknown>) ?? {};
          update.prod_data = { ...current, ...entries };
        }
      }

      const { error } = await db.from("property_attributes").update(update).eq("id", attributeId);
      if (error) throw error;
      return { deferred: false };
    },
    onSuccess: (res) => {
      // Catalog wasn't ready — keep the dirty state + changed ids and retry, so
      // the canonical prod_data write isn't skipped.
      if (res.deferred) { scheduleSave(); return; }
      setIsDirty(false);
      changedIdsRef.current.clear();
      queryClient.invalidateQueries({ queryKey: ["property-attributes-list"] });
      queryClient.invalidateQueries({ queryKey: ["property-attributes"] });
      toast.success("Changes saved", { duration: 2000 });
    },
    onError: (err: any) => {
      console.error("Failed to save:", err);
      toast.error("Failed to save changes");
    },
  });

  // Keep a ref in sync so the mutation always reads latest fields
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  /** Force save now (e.g., before navigating away) */
  const saveNow = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (isDirty && attributeId) {
      saveMutation.mutate();
    }
  }, [isDirty, attributeId, saveMutation]);

  return {
    fields,
    isDirty,
    isSaving: saveMutation.isPending,
    initializeFields,
    updateFieldValue,
    updateFieldMetadata,
    saveNow,
  };
}

// ── Helpers ──────────────────────────────────────────────────

/** leaf.type → prod_data input_type (snapshot nicety; structure comes from template). */
function inputTypeFor(leafType: string | undefined): string | null {
  switch (leafType) {
    case "select": return "single_select";
    case "multiSelect": return "multi_select";
    case "input":
    case "area": return "text";
    case "photos": return "photos";
    default: return leafType ?? null;
  }
}

/** Whether a leaf value counts as answered (mirrors the gate's isAnsweredValue). */
function leafAnswered(type: string | undefined, value: unknown): boolean {
  if (value == null) return false;
  if (type === "multiSelect" || type === "photos") return Array.isArray(value) && value.length > 0;
  if (type === "area") return String(value).replace(/<[^>]*>/g, " ").trim() !== "";
  if (typeof value === "boolean" || typeof value === "number") return true;
  return String(value).trim() !== "";
}

/**
 * Build prod_data entries for the changed leaves, keyed by question_id (catalog
 * dev code, else ref_id). Writes the SELF-DESCRIBING structure (`field_type`,
 * `options`, `section_path`, `order`) so edited entries keep rendering from
 * prod_data alone. Surveyor-sourced + auto-accepted, matching the backfill shape.
 */
function collectChangedEntries(
  fields: AttributeField[],
  changedIds: Set<string>,
  catalog: CatalogCrosswalk,
  now: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const counter = { n: 0 };
  const walk = (nodes: AttributeField[], path: Array<{ id: string; title: string }>) => {
    for (const node of nodes) {
      if (node.type === "node") {
        walk(node.fields ?? [], [...path, { id: node.id, title: node.title ?? "" }]);
        continue;
      }
      const order = counter.n++; // pre-order index over the whole survey
      if (!changedIds.has(node.id)) continue;
      const leaf = node as any;
      const refId: string = leaf.ref_id ?? leaf.id;
      const xref = catalog.get(refId);
      const questionId = xref?.questionId ?? refId;
      const evidence = Array.isArray(leaf.evidence)
        ? (leaf.evidence as unknown[])
            .filter((u): u is string => typeof u === "string" && u.length > 0)
            .map((url) => ({ type: "image", url, source: "surveyor" }))
        : [];
      const answered = leafAnswered(leaf.type, leaf.value);
      out[questionId] = {
        final_survey_answer: leaf.value ?? null,
        final_source: "surveyor",
        final_tier: "A",
        status: answered ? "approved" : "unanswered",
        review_note: null,
        reviewed_by: "site-survey-editor",
        reviewed_at: now,
        question_id: questionId,
        leaf_id: refId,
        question_title: leaf.title ?? null,
        section: xref?.section ?? (path.length ? path[path.length - 1].title : null),
        t_class: xref?.tClass ?? null,
        input_type: inputTypeFor(leaf.type),
        // Self-describing structure (so prod_data renders without a template).
        field_type: leaf.type ?? null,
        options: Array.isArray(leaf.options) && leaf.options.length ? leaf.options : null,
        section_path: path,
        order,
        human_answer: leaf.value ?? null,
        human_confidence: leaf.confidence ?? null,
        ai_answer: null,
        ai_run_label: null,
        reviewer_confidence: null,
        evidence,
      };
    }
  };
  walk(fields, []);
  return out;
}

function mergeMetadataInTree(
  tree: AttributeField[],
  fieldId: string,
  patch: { confidence?: number | null; evidence?: string[]; notes?: string | null }
): boolean {
  for (const node of tree) {
    if (node.id === fieldId && node.type !== "node") {
      const leaf = node as any;
      if (patch.confidence !== undefined) leaf.confidence = patch.confidence;
      if (patch.evidence !== undefined) leaf.evidence = patch.evidence;
      if (patch.notes !== undefined) leaf.notes = patch.notes;
      return true;
    }
    if (node.type === "node" && node.fields) {
      if (mergeMetadataInTree(node.fields, fieldId, patch)) return true;
    }
  }
  return false;
}

function updateValueInTree(
  tree: AttributeField[],
  fieldId: string,
  newValue: string | number | boolean | string[] | null
): boolean {
  for (const node of tree) {
    if (node.id === fieldId && node.type !== "node") {
      (node as any).value = newValue;
      return true;
    }
    if (node.type === "node" && node.fields) {
      if (updateValueInTree(node.fields, fieldId, newValue)) return true;
    }
  }
  return false;
}
